import { useCallback, useEffect, useState } from 'react';

import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import { alpha } from '@mui/material/styles';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { getAllUsers } from 'src/redux/slices/users';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getReorderSettings } from 'src/redux/slices/reorder-settings';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IReorderSettingsRow, ReorderSettingsTab } from 'src/types/reorder-settings';

import ReorderSettingsTableRow from '../reorder-settings-table-row';
import ReorderSettingsTableToolbar from '../reorder-settings-table-toolbar';

const TABS: { value: ReorderSettingsTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'packaging', label: 'Packaging Items' },
  { value: 'stationery', label: 'Stationery' },
];

const TABLE_HEAD = [
  { id: 'itemCode', label: 'Item Code', align: 'left' },
  { id: 'itemName', label: 'Item Name', align: 'left' },
  { id: 'quantity', label: 'Current Qty', align: 'right' },
  { id: 'reorderThreshold', label: 'Low Stock Qty', align: 'right' },
  { id: 'newOrderQty', label: 'New Order Qty', align: 'left' },
  { id: 'orderBy', label: 'Order By', align: 'left' },
  { id: 'hiddenFromMyTasks', label: 'Occasional Item', align: 'center' },
];

export default function ReorderSettingsListView() {
  const { status, reorderSettings, error } = useAppSelector((state) => state.reorderSettings);
  const { allUsers } = useAppSelector((state) => state.users);

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const [generalSearch, setGeneralSearch] = useState('');

  const [tab, setTab] = useState<ReorderSettingsTab>('all');

  const [sort, setSort] = useState<{ sortBy: string; order: 'asc' | 'desc' }>({
    sortBy: 'itemName',
    order: 'asc',
  });

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ReorderSettingsTab) => {
      table.onResetPage();
      setTab(newValue);
    },
    [table]
  );

  const handleSearch = useCallback(
    (value: string) => {
      table.onResetPage();
      setGeneralSearch(value);
    },
    [table]
  );

  const fetchReorderSettings = useCallback(() => {
    dispatch(
      getReorderSettings({
        page,
        limit: rowsPerPage,
        type: tab,
        generalSearch,
        sortBy: sort.sortBy,
        sort: sort.order,
      })
    );
  }, [dispatch, page, rowsPerPage, tab, generalSearch, sort]);

  useEffect(() => {
    fetchReorderSettings();
  }, [fetchReorderSettings]);

  // Fetched once — feeds every row's Order By dropdown.
  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs heading="Reorder Settings" links={[]} sx={{ mb: { xs: 3 } }} />

      <Card>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            px: 2.5,
            boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
        >
          {TABS.map((tabItem) => (
            <Tab key={tabItem.value} value={tabItem.value} label={tabItem.label} />
          ))}
        </Tabs>

        <ReorderSettingsTableToolbar generalSearch={generalSearch} onSearch={handleSearch} />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={reorderSettings.items.length}
                numSelected={0}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {reorderSettings.items.map((row: IReorderSettingsRow) => (
                      <ReorderSettingsTableRow key={row._id} row={row} allUsers={allUsers} />
                    ))}
                  </>
                )}

                {status !== 'failure' && reorderSettings.count === 0 && (
                  <TableNoData message="No items found" notFound={reorderSettings.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={reorderSettings.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
